import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Type, Border, Shadow, Color } from '../../app/StyleGuide';

type RichTextToolbarProps = {
  isVisible: boolean;
  inline?: boolean;
  children: React.ReactNode;
  role?: string;
  delay?: number;
};

export const RichTextToolbar = ({
  isVisible,
  inline = false,
  children,
  role = 'toolbar',
  delay = 500,
}: RichTextToolbarProps) => {
  const [internalVisible, setInternalVisible] = useState(isVisible);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isVisible) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setInternalVisible(true);
    } else {
      hideTimeoutRef.current = setTimeout(() => {
        setInternalVisible(false);
      }, delay);
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [isVisible, delay]);

  return (
    <ToolbarContainer isVisible={internalVisible} inline={inline} role={role}>
      {children}
    </ToolbarContainer>
  );
};

const ToolbarContainer = styled.div<{ isVisible: boolean; inline?: boolean }>`
  display: flex;
  gap: ${Grid._1};
  background: white;
  color: ${Type.Color.dark};
  padding: ${Grid._2};
  border-radius: ${Border.radius};
  box-shadow: ${Shadow.light};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  font-size: ${Type.Scale._2};
  ${props =>
    !props.inline &&
    `
    position: absolute;
    bottom: 100%;
    left: 0;
    margin-bottom: ${Grid._2};
    transform: ${props.isVisible ? 'translateY(0)' : 'translateY(10px)'};
    transition: opacity 0.2s ease, transform 0.2s ease;
  `}
  ${props =>
    props.inline &&
    `
    border: 1px solid ${Color.Blue._20};
    border-width: 1px 1px 0 1px;
    background-color: ${Color.Gray._05};
    border-radius: ${Border.radius} ${Border.radius} 0 0;
    width: 100%;
  `}
`;

export const ToolbarButton = styled.button.attrs({
  type: 'button',
  onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault(),
})`
  background: none;
  border: none;
  padding: ${Grid._1};
  cursor: pointer;
  border-radius: ${Border.radius};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Grid._6};
  height: ${Grid._6};
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export const ToolbarDivider = styled.div`
  width: 1px;
  height: auto;
  background-color: ${Color.Blue._15};
  margin: ${Grid._2} ${Grid._1};
`;
